import React, { useEffect } from 'react';
import { Helmet } from "react-helmet-async";
import { Phone, Mail, MapPin, Sparkles, User } from 'lucide-react';

const accommodations = [
  {
    category: "Jesuit Scholastics",
    title: "Vidyajyoti Jesuit Residence",
    contactPerson: "Rector, Vidyajyoti",
    address: "4A Raj Niwas Marg, Delhi – 110 054",
    email: "rectorvj@gmail.com",
    details: "Provides on-campus residential community life and formation for Jesuit scholastics pursuing theological studies.",
    accent: "#1a2b56"
  },
  {
    category: "Women Religious & Lay Women",
    title: "St. Stephen's Hostel for Women",
    contactPerson: "Rector & Principal, Vidyajyoti",
    address: "Near Vidyajyoti College Campus, Delhi",
    email: "rectorvj@gmail.com / principalvj@gmail.com",
    details: "Affiliated residential facility providing safe, dedicated accommodation for women religious and lay students in close proximity to the faculty.",
    accent: "#79031d"
  },
  {
    category: "Diocesan Seminarians & Religious",
    title: "Pratiksha Study House",
    contactPerson: "Fr. Jose T.J (Rector)",
    address: "Pratiksha, 8 Vishwa Apartments, New Delhi – 110 054",
    phone: "(+91) 98718 11880",
    email: "jobytj@gmail.com",
    details: "Community living and academic residence for diocesan seminarians and religious brothers doing theology at Vidyajyoti.",
    accent: "#c69214"
  },
  {
    category: "Indian Missionary Society (IMS)",
    title: "Jnana Bharati Gurukul",
    contactPerson: "Fr. Mahendra Paul, IMS (Rector)",
    address: "Jnana Bharati Gurukul, A-1319, Lok Vihar, Pitampura, New Delhi – 110 034",
    phone: "(+91) 94527 09755",
    email: "mahendrapaulims@gmail.com",
    details: "Study residence and community for IMS seminarians and student priests.",
    accent: "#1a2b56"
  },
  {
    category: "Syro-Malabar Seminarians",
    title: "Vidyajyoti Niwas (Faridabad Diocese)",
    contactPerson: "Fr. Joseph Tharayil (Frijo)",
    address: "Vidyajyoti Niwas, Model Town, Near Naini Lake, New Delhi – 110 009",
    phone: "(+91) 62352 40204",
    email: "rectorvj@gmail.com",
    details: "Residential study house for seminarians and students of the Diocese of Faridabad.",
    accent: "#79031d"
  }
];

export default function Accomodation() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="py-5" style={{ background: '#f8fafc', minHeight: '85vh' }}>
      <Helmet>
        <title>Student Accommodation - Vidyajyoti College of Theology</title>
        <meta name="description" content="Accommodation options for Jesuits, Women Religious, Diocesan Seminarians, IMS, and other student groups at Vidyajyoti." />
        <meta name="keywords" content="Vidyajyoti accommodation, student hostel Delhi, seminary hostel Delhi, theological student residence" />
        <link rel="canonical" href="/services/accomodation" />
      </Helmet>

      <div className="container">
        {/* Page Header */}
        <div className="text-center mx-auto pb-4" style={{ maxWidth: '750px' }}>
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3" style={{ background: 'rgba(26, 43, 86, 0.08)', color: '#1a2b56', fontSize: '0.85rem', fontWeight: 600, border: '1px solid rgba(26, 43, 86, 0.2)' }}>
            <Sparkles size={14} /> Student Life &amp; Facilities
          </div>
          <h1 className="display-6 fw-bold mb-3" style={{ color: '#1a2b56', fontFamily: "'Outfit', sans-serif" }}>
            Student Accommodation
          </h1>
          <p className="lead text-muted" style={{ fontSize: '1.05rem', lineHeight: '1.7' }}>
            Vidyajyoti and its affiliated study houses provide dedicated residential facilities for Jesuits, religious sisters, diocesan seminarians, and student priests.
          </p>
        </div>

        {/* Accommodation Cards Grid */}
        <div className="row g-4 justify-content-center">
          {accommodations.map((item, index) => (
            <div key={index} className="col-lg-6 col-xl-4">
              <div 
                className="card h-100 border-0 shadow-sm rounded-4 p-4 bg-white d-flex flex-column"
                style={{
                  borderTop: `4px solid ${item.accent}`,
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)';
                }}
              >
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="badge px-2.5 py-1 rounded-pill small" style={{ background: 'rgba(26,43,86,0.06)', color: item.accent, fontWeight: 600 }}>
                    {item.category}
                  </span>
                </div>

                <h4 className="fw-bold mb-2" style={{ color: '#1a2b56', fontSize: '1.2rem' }}>
                  {item.title}
                </h4>

                <p className="text-muted small mb-4" style={{ lineHeight: '1.6' }}>
                  {item.details}
                </p>

                <div className="mt-auto pt-3 border-top d-flex flex-column gap-2">
                  <div className="d-flex align-items-start gap-2 text-muted small">
                    <User size={15} className="flex-shrink-0 mt-0.5 text-primary" />
                    <span><strong>Contact:</strong> {item.contactPerson}</span>
                  </div>

                  {item.address && (
                    <div className="d-flex align-items-start gap-2 text-muted small">
                      <MapPin size={15} className="flex-shrink-0 mt-0.5 text-danger" />
                      <span>{item.address}</span>
                    </div>
                  )}

                  {item.phone && (
                    <div className="d-flex align-items-center gap-2 text-muted small">
                      <Phone size={15} className="flex-shrink-0 text-success" />
                      <a href={`tel:${item.phone}`} className="text-decoration-none fw-semibold text-dark">
                        {item.phone}
                      </a>
                    </div>
                  )}

                  {item.email && (
                    <div className="d-flex align-items-center gap-2 text-muted small">
                      <Mail size={15} className="flex-shrink-0 text-warning" />
                      <a href={`mailto:${item.email.split(' ')[0]}`} className="text-decoration-none fw-semibold text-truncate" style={{ color: '#79031d' }}>
                        {item.email}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
